/**
 * Saudi phone number validation and formatting (unified across all apps).
 * - Fixed country code: +966
 * - User input: 9 digits (no leading 0) or 10 digits (with leading 0)
 * - Stored/display format: +966 followed by 9 digits (leading 0 stripped if present)
 */

export const SAUDI_COUNTRY_CODE = '+966'
export const SAUDI_PHONE_DIGITS_LENGTH = 9

/**
 * Strip non-digits and optional leading 0 from Saudi number.
 * Returns only the 9-digit part (e.g. "501234567").
 */
export function cleanSaudiPhoneInput(value: string): string {
  const digits = value.replace(/\D/g, '')
  if (digits.startsWith('0') && digits.length > 1) {
    return digits.slice(1)
  }
  if (digits.startsWith('966') && digits.length > 9) {
    return digits.slice(3)
  }
  if (digits.startsWith('966')) {
    return digits.slice(3)
  }
  return digits
}

/**
 * Validate Saudi phone: 9 digits (no leading 0) or 10 digits (with leading 0).
 * Returns validation result with optional normalized value.
 */
export function validateSaudiPhone(value: string): {
  valid: boolean
  normalized?: string
  errorKey?: string
} {
  if (typeof value !== 'string') {
    return { valid: false, errorKey: 'validation.phoneSaudiFormat' }
  }
  const cleaned = cleanSaudiPhoneInput(value.trim())
  if (cleaned.length === 0) {
    return { valid: false, errorKey: 'validation.phoneRequired' }
  }
  if (!/^\d+$/.test(cleaned)) {
    return { valid: false, errorKey: 'validation.phoneSaudiFormat' }
  }
  // Allow 9 digits (no leading 0) or 10 digits (0 + 9 digits)
  if (cleaned.length !== 9) {
    return { valid: false, errorKey: 'validation.phoneSaudiFormat' }
  }
  const normalized = `${SAUDI_COUNTRY_CODE}${cleaned}`
  return { valid: true, normalized }
}

/**
 * Normalize input to storage format: +966 + 9 digits.
 * Use after validation. Returns null if invalid.
 */
export function normalizeSaudiPhoneForStorage(value: string): string | null {
  const result = validateSaudiPhone(value)
  return result.valid ? result.normalized ?? null : null
}

/**
 * Format for display (e.g. +966 50 123 4567). Input can be raw or already +966...
 */
export function formatSaudiPhoneForDisplay(value: string): string {
  const cleaned = cleanSaudiPhoneInput(value)
  if (cleaned.length !== 9) return value.trim()
  return `${SAUDI_COUNTRY_CODE} ${cleaned.slice(0, 2)} ${cleaned.slice(2, 5)} ${cleaned.slice(5)}`
}

